import { create } from 'zustand';
import { getInitialCode } from '@/stores/useBoardEditorFileStore';
import { XAxisMode, YAxisMode } from '@/types/boards';
import { BT_NAME_ANNOUNCE_PREFIX } from '@/constants/constants';
import { BOARD_CONFIG, BoardType } from '@/(routers)/boards/editor/block/blockConfig';

let pendingMessages: Array<{ timestamp: string; data: string; isSent: boolean }> = [];
let rafId: number | null = null;
let pendingSerialChunks: string[] = [];
let serialRafId: number | null = null;

export type { BoardType };

export interface DetectedBoard {
    type: BoardType;
    port: string;
    is_bluetooth?: boolean;
    bluetooth_name?: string;
    // USBデバイス自体のシリアル番号(bridgeがport.serial_numberから取得)。OSのポート/デバイスパスは
    // 環境によっては別の実機に差し替えても変わらないことがあるため(特にMac)、
    // 「同じポートのまま別の実機に入れ替わった」ことを検出するために使う。
    serial_number?: string;
}

export interface PairedBoard {
    id: string;
    boardId: string;
    boardName: string;
    btName: string;
    pairedAt: string;
    lastUsedAt: string;
    chipId?: string;
}

// GET /boards/identify のレスポンス(focs/backend/api/services/focs/models/boards.pyのBoardIdentifyResponseと同じ形)
export interface BoardIdentifyResponse {
    is_registered: boolean;
    board_id: number | null;
    // m_boards.cd(UNIQUE)。BTデバイス名の5桁ゼロ埋め部分に使う。未登録の間はnull
    cd: number | null;
    board_name: string | null;
    hub_id: number | null;
    hub_name: string | null;
    hub_type_cd: string | null;
    hub_type_name: string | null;
    bluetooth_device_name: string | null;
}

export type EditorMode = 'code' | 'block';
export type OsType = 'windows' | 'mac' | 'chromebook';

interface BuildResult {
    filename: string;
    artifact_b64: string;
    message: string;
    builtAt: string;
}

export interface MonitorMessage {
    timestamp: string;
    data: string;
    isSent?: boolean;
}

export type UploadOverlayStatus = 'checking' | 'compiling' | 'compiled' | 'sending' | 'writing' | 'success' | 'error';

const STATUS_ORDER: Record<UploadOverlayStatus, number> = {
    checking: 1,
    compiling: 2,
    compiled: 3,
    sending: 4,
    writing: 5,
    success: 6,
    error: 999,
};

function clamp(n: number, min: number, max: number) {
    return Math.max(min, Math.min(max, n));
}

function mapLocalToGlobal(localPct: number, phase: 'sending' | 'writing') {
    const local = clamp(localPct, 0, 100);
    if (local === 60) return 70;
    if (local === 80) return 85;
    if (local === 100) return 99;
    if (phase === 'sending') {
        const p = clamp(local / 60, 0, 1);
        return Math.floor(60 + p * 10);
    }
    const p = clamp((local - 60) / 40, 0, 1);
    return Math.floor(70 + p * 29);
}

let fakeTimer: number | null = null;

function stopFakeProgress() {
    if (fakeTimer !== null) {
        clearInterval(fakeTimer);
        fakeTimer = null;
    }
}

function startFakeProgress(max: number, intervalMs: number) {
    stopFakeProgress();
    fakeTimer = window.setInterval(() => {
        const st = useBoardEditorStore.getState();
        if (st.uploadStatus === 'success' || st.uploadStatus === 'error') {
            stopFakeProgress();
            return;
        }
        if (st.uploadProgress >= max) return;
        useBoardEditorStore.setState({
            uploadProgress: clamp(st.uploadProgress + 1, 0, 99),
        });
    }, intervalMs);
}

let uploadTimeoutTimer: number | null = null;

function stopUploadTimeout() {
    if (uploadTimeoutTimer !== null) {
        clearTimeout(uploadTimeoutTimer);
        uploadTimeoutTimer = null;
    }
}

/*
通常ブリッジはエラーを返すが、タイムアウトにより沈黙するケースがある。その場合、想定時間を
過ぎたら自己判断でエラーとする。ブリッジ側(schoomy_bridge/bridge/handlers/serial_device.py)の
subprocessタイムアウト(avrdude=60秒・esptool=120秒)より必ず長くすること。短いと、ブリッジ側は
正常終了するはずの処理をフロントが待ちきれず先にタイムアウト扱いにしてしまう。
*/
const UPLOAD_TIMEOUT_MS: Record<string, number> = {
    orange: 70_000,
    blue: 130_000,
};

function startUploadTimeout() {
    stopUploadTimeout();
    const board = useBoardEditorStore.getState().board;
    const ms = UPLOAD_TIMEOUT_MS[board] ?? 150_000;
    uploadTimeoutTimer = window.setTimeout(() => {
        uploadTimeoutTimer = null;
        const st = useBoardEditorStore.getState();
        if (st.uploadStatus === 'success' || st.uploadStatus === 'error') return;
        stopFakeProgress();
        useBoardEditorStore.setState({
            uploadStatus: 'error',
            uploadMessage: '書き込みがタイムアウトしました。再度お試しください。',
        });
    }, ms);
}

function formatTimestamp(): string {
    const now = new Date();
    const hh = String(now.getHours()).padStart(2, '0');
    const mi = String(now.getMinutes()).padStart(2, '0');
    const ss = String(now.getSeconds()).padStart(2, '0');
    const ms = String(now.getMilliseconds()).padStart(3, '0');
    return `${hh}:${mi}:${ss}.${ms}`;
}

function stripAnsi(text: string): string {
    return text.replace(/\x1b\[[0-9;]*m/g, '');
}

function getInitialBoard(): BoardType {
    try {
        const stored = localStorage.getItem('KEY-BOARD-EDITOR-CONFIG');
        if (stored) {
            const parsed = JSON.parse(stored);
            return (parsed?.state?.defaultBoard as BoardType) ?? 'orange';
        }
    } catch {}
    return 'orange';
}

interface EditorState {
    board: BoardType;
    port: string | null;
    code: string;
    editorMode: EditorMode;
    lastEditedAt: string | null;
    blockZoomScale: number;
    isBuilding: boolean;
    lastBuild: BuildResult | null;
    hasSelectedBoard: boolean;
    isWebSocketConnected: boolean;
    isBridgeConnected: boolean;
    // ブリッジの初回接続判定(成功/失敗)が完了したか。完了前はisBridgeConnectedの楽観的初期値による
    // 一瞬のチラつきを避けるため、UI側で表示可否の判定に使う。
    isBridgeStatusResolved: boolean;
    detectedBoards: DetectedBoard[];
    pairedBoards: PairedBoard[];
    onlineSystemIds: string[];
    logs: string[];
    isUploading: boolean;
    uploadProgress: number;
    uploadStatus: UploadOverlayStatus;
    uploadMessage: string;
    // コンパイル待ちの概算人数(0または未取得はnull)。バッジ表示用にuploadMessageとは分けて持つ。
    uploadQueueAhead: number | null;
    // 現在ポーリング中のコンパイルjob_id(未取得/終了後はnull)。順番待ちキャンセルボタンから
    // /bridge/build/cancel/{job_id}を呼ぶために保持する。
    activeCompileJobId: string | null;
    activeUploadRequestId: string | null;
    monitorMessages: MonitorMessage[];
    pendingSerialLine: string;
    pendingSerialLineTimestamp: string;
    serialReceivedCount: number;
    monitorAutoScroll: boolean;
    isMonitorOpen: boolean;
    osType: OsType;
    // 接続中のブリッジ自身が報告してきたバージョン(request_bridge_version/bridge_version)。
    // ブリッジ未接続時や、Web Serial API利用時はnullのまま(ブリッジ自体を使わないため)。
    bridgeVersion: string | null;
    // ブリッジが自身のbridge_update.pyの定期チェックでキャッシュしている「今知りうる最新
    // バージョン」(request_latest_bridge_version/latest_bridge_version)。fox自身は
    // /bridge/update(ブリッジ専用認証が必要)を直接呼べないため、ブリッジ経由で取得する。
    // 古いブリッジ(このメッセージタイプ未対応)や、ブリッジがまだ一度もチェックできていない
    // 間はnullのまま。
    latestBridgeVersion: string | null;
    // 設定モーダルの「今すぐ更新を確認」ボタンから、BoardToolbar.tsx側が保持する
    // useBoardWebSocketLocalのcheckBridgeUpdateNowを呼び出すための橋渡し。モーダルと
    // ツールバーは別コンポーネントでWebSocket接続を共有していないため、関数そのものを
    // ストア経由で受け渡す(BoardToolbar.tsxがマウント中のみ非null)。
    triggerBridgeUpdateCheck: (() => void) | null;
    isCheckingBridgeUpdate: boolean;
    // 直近の手動更新チェックの結果メッセージ(「既に最新バージョンです」等)。
    bridgeUpdateCheckMessage: string | null;
    monitorXMode: XAxisMode;
    monitorTimeWindow: number;
    monitorYMode: YAxisMode;
    monitorYMin: number | undefined;
    monitorYMax: number | undefined;
    savedFileId: string | null;
    monitorSelectedPort: string | null;
    monitorSelectedBoard: BoardType | null;
    // 救済リスト(detectedBoardsに載らないペアリング済みBluetoothポート)から選んだ場合のみ設定する。
    // これらのポートはdetectedBoardsに存在しないため、is_bluetooth/bluetooth_nameを参照できない。
    monitorSelectedBluetoothName: string | null;
    // 接続確定時に1回だけ実機に問い合わせたchip_idと、それを使って取得したボード情報のキャッシュ。
    // 切断・別ボードへの繋ぎ直し時にはnullへ戻すこと(古い情報を残さないため)。
    chipId: number | null;
    boardIdentity: BoardIdentifyResponse | null;
    // chip_id問い合わせの進行状況。'timed_out'(問い合わせが成功せず無応答)は「まだ新しい応答処理入りの
    // ファームウェアが書き込まれていない」ことを意味し、書き込み更新モーダルの表示条件に使う。
    chipIdQueryStatus: 'idle' | 'pending' | 'succeeded' | 'timed_out';
    setChipId: (chipId: number | null) => void;
    setBoardIdentity: (identity: BoardIdentifyResponse | null) => void;
    setChipIdQueryStatus: (status: 'idle' | 'pending' | 'succeeded' | 'timed_out') => void;
    setBoard: (board: BoardType) => void;
    getBaud: () => number;
    setPort: (port: string | null) => void;
    setCode: (code: string) => void;
    setEditorMode: (mode: EditorMode) => void;
    setLastEditedAt: (time: string) => void;
    setBlockZoomScale: (scale: number) => void;
    setIsBuilding: (building: boolean) => void;
    setLastBuild: (result: BuildResult | null) => void;
    setHasSelectedBoard: (selected: boolean) => void;
    setWebSocketConnected: (connected: boolean) => void;
    setIsBridgeConnected: (connected: boolean) => void;
    setIsBridgeStatusResolved: (resolved: boolean) => void;
    setDetectedBoards: (boards: DetectedBoard[]) => void;
    setPairedBoards: (boards: PairedBoard[]) => void;
    setOnlineSystemIds: (ids: string[]) => void;
    appendLog: (message: string) => void;
    appendErrorLog: (message: unknown) => void;
    clearLogs: () => void;
    setIsUploading: (isUploading: boolean) => void;
    setUploadProgress: (progress: number) => void;
    setUploadStatus: (status: UploadOverlayStatus) => void;
    setUploadMessage: (message: string) => void;
    setUploadQueueAhead: (ahead: number | null) => void;
    setActiveCompileJobId: (id: string | null) => void;
    setActiveUploadRequestId: (id: string | null) => void;
    resetUpload: () => void;
    applyUploadEvent: (e: {
        source: 'cloud' | 'localhost' | 'webserialapi';
        kind: 'phase' | 'progress' | 'result';
        status?: UploadOverlayStatus;
        progress?: number;
        phase?: 'sending' | 'writing';
        message?: string;
        success?: boolean;
        errorMessage?: string;
    }) => void;
    appendMonitorMessage: (data: string, isSent?: boolean) => void;
    appendMonitorMessageBatch: (message: string[]) => void;
    appendSerialChunk: (chunk: string, onLines?: (lines: string[]) => void) => void;
    clearSerialChunks: () => void;
    clearMonitorMessages: () => void;
    setMonitorAutoScroll: (enabled: boolean) => void;
    setIsMonitorOpen: (enabled: boolean) => void;
    resetEditor: () => void;
    setOsType: (type: OsType) => void;
    setBridgeVersion: (version: string | null) => void;
    setLatestBridgeVersion: (version: string | null) => void;
    setTriggerBridgeUpdateCheck: (fn: (() => void) | null) => void;
    setIsCheckingBridgeUpdate: (checking: boolean) => void;
    setBridgeUpdateCheckMessage: (message: string | null) => void;
    setMonitorXMode: (mode: XAxisMode) => void;
    setMonitorTimeWindow: (time: number) => void;
    setMonitorYMode: (mode: YAxisMode) => void;
    setMonitorYMin: (val: number | undefined) => void;
    setMonitorYMax: (val: number | undefined) => void;
    flashSaved: (fileId: string) => void;
    clearSavedFileId: () => void;
    setMonitorSelectedPort: (port: string | null) => void;
    setMonitorSelectedBoard: (board: BoardType | null) => void;
    setMonitorSelectedBluetoothName: (name: string | null) => void;
}

const initialBoard = getInitialBoard();

export const useBoardEditorStore = create<EditorState>((set, get) => ({
    board: initialBoard,
    port: null,
    code: getInitialCode(initialBoard),
    editorMode: 'block',
    lastEditedAt: null,
    blockZoomScale: 0.8,
    isBuilding: false,
    lastBuild: null,
    hasSelectedBoard: false,
    isWebSocketConnected: false,
    isBridgeConnected: true,
    isBridgeStatusResolved: false,
    detectedBoards: [],
    pairedBoards: [],
    onlineSystemIds: [],
    logs: [],
    isUploading: false,
    uploadProgress: 0,
    uploadStatus: 'checking',
    uploadMessage: '',
    uploadQueueAhead: null,
    activeCompileJobId: null,
    activeUploadRequestId: null,
    monitorMessages: [],
    pendingSerialLine: '',
    pendingSerialLineTimestamp: '',
    serialReceivedCount: 0,
    monitorAutoScroll: true,
    isMonitorOpen: false,
    osType: 'windows',
    bridgeVersion: null,
    latestBridgeVersion: null,
    triggerBridgeUpdateCheck: null,
    isCheckingBridgeUpdate: false,
    bridgeUpdateCheckMessage: null,
    monitorXMode: 'count',
    monitorTimeWindow: 10,
    monitorYMode: 'auto',
    monitorYMin: undefined,
    monitorYMax: undefined,
    savedFileId: null,
    monitorSelectedPort: null,
    monitorSelectedBoard: null,
    monitorSelectedBluetoothName: null,
    chipId: null,
    boardIdentity: null,
    chipIdQueryStatus: 'idle',
    setChipId: (chipId) => set({ chipId }),
    setBoardIdentity: (identity) => set({ boardIdentity: identity }),
    setChipIdQueryStatus: (status) => set({ chipIdQueryStatus: status }),
    setBoard: (board) => set({ board }),
    getBaud: () => BOARD_CONFIG[get().board].baud,
    setPort: (port) => set({ port }),
    setCode: (code) => set({ code, lastEditedAt: new Date().toISOString() }),
    setEditorMode: (mode) => set({ editorMode: mode }),
    setLastEditedAt: (time) => set({ lastEditedAt: time }),
    setBlockZoomScale: (scale) => set({ blockZoomScale: scale }),
    setIsBuilding: (building) => set({ isBuilding: building }),
    setLastBuild: (result) => set({ lastBuild: result }),
    setHasSelectedBoard: (selected) => set({ hasSelectedBoard: selected }),
    setWebSocketConnected: (connected) => set({ isWebSocketConnected: connected }),
    setIsBridgeConnected: (connected) => set({ isBridgeConnected: connected }),
    setIsBridgeStatusResolved: (resolved) => set({ isBridgeStatusResolved: resolved }),
    setDetectedBoards: (boards) => set({ detectedBoards: boards }),
    setPairedBoards: (boards) => set({ pairedBoards: boards }),
    setOnlineSystemIds: (ids) => set({ onlineSystemIds: ids }),
    setOsType: (type) => set({ osType: type }),
    setBridgeVersion: (version) => set({ bridgeVersion: version }),
    setLatestBridgeVersion: (version) => set({ latestBridgeVersion: version }),
    setTriggerBridgeUpdateCheck: (fn) => set({ triggerBridgeUpdateCheck: fn }),
    setIsCheckingBridgeUpdate: (checking) => set({ isCheckingBridgeUpdate: checking }),
    setBridgeUpdateCheckMessage: (message) => set({ bridgeUpdateCheckMessage: message }),
    setMonitorXMode: (mode) => set({ monitorXMode: mode }),
    setMonitorTimeWindow: (time) => set({ monitorTimeWindow: time }),
    setMonitorYMode: (mode) => set({ monitorYMode: mode }),
    setMonitorYMin: (val) => set({ monitorYMin: val }),
    setMonitorYMax: (val) => set({ monitorYMax: val }),
    setMonitorSelectedPort: (port) => set({ monitorSelectedPort: port }),
    setMonitorSelectedBoard: (board) => set({ monitorSelectedBoard: board }),
    setMonitorSelectedBluetoothName: (name) => set({ monitorSelectedBluetoothName: name }),
    flashSaved: (fileId) => {
        set({ savedFileId: fileId });
        setTimeout(() => {
            set((state) => (state.savedFileId === fileId ? { savedFileId: null } : {}));
        }, 1500);
    },
    clearSavedFileId: () => set({ savedFileId: null }),

    appendLog: (message) => {
        const timestamp = formatTimestamp();
        const cleaned = stripAnsi(message);
        set((state) => ({ logs: [...state.logs, `${timestamp}   ${cleaned}`] }));
    },

    appendErrorLog: (message: unknown) => {
        const timestamp = formatTimestamp();
        const msg = message instanceof Error ? message.message : String(message);
        const cleaned = stripAnsi(msg);
        set((state) => ({ logs: [...state.logs, `${timestamp}   ❌ ${cleaned}`] }));
    },

    clearLogs: () => set({ logs: [] }),

    setIsUploading: (isUploading) => set({ isUploading }),
    setUploadProgress: (uploadProgress) => set({ uploadProgress: clamp(uploadProgress, 0, 100) }),
    setUploadStatus: (uploadStatus) => set({ uploadStatus }),
    setUploadMessage: (uploadMessage) => set({ uploadMessage }),
    setUploadQueueAhead: (uploadQueueAhead) => set({ uploadQueueAhead }),
    setActiveCompileJobId: (id) => set({ activeCompileJobId: id }),
    setActiveUploadRequestId: (id) => set({ activeUploadRequestId: id }),

    resetUpload: () => {
        stopFakeProgress();
        stopUploadTimeout();
        set({
            uploadProgress: 0,
            uploadStatus: 'checking',
            uploadMessage: '',
            uploadQueueAhead: null,
            activeCompileJobId: null,
        });
    },

    applyUploadEvent: (e) => {
        const st = get();
        let nextStatus: UploadOverlayStatus = st.uploadStatus;
        let nextProgress = st.uploadProgress;
        let nextMessage = st.uploadMessage;

        if (e.kind === 'phase') {
            if (e.status) nextStatus = e.status;
            if (e.message) nextMessage = e.message;
            if (typeof window !== 'undefined') {
                if (e.status === 'checking') {
                    stopFakeProgress();
                    nextProgress = Math.max(nextProgress, 5);
                }
                if (e.status === 'compiling') {
                    stopFakeProgress();
                    nextProgress = Math.max(nextProgress, 10);
                    startFakeProgress(50, 220);
                }
                if (e.status === 'compiled') {
                    stopFakeProgress();
                    nextProgress = Math.max(nextProgress, 60);
                }
                if (e.status === 'sending') {
                    stopFakeProgress();
                    nextProgress = Math.max(nextProgress, 60);
                    startFakeProgress(70, 260);
                }
            }
        }

        if (e.kind === 'progress') {
            stopFakeProgress();
            const phase = e.phase ?? 'writing';
            const local = typeof e.progress === 'number' ? e.progress : 0;
            nextStatus = phase === 'sending' ? 'sending' : 'writing';
            nextProgress = Math.max(nextProgress, mapLocalToGlobal(local, phase));
            if (e.message) nextMessage = e.message;
            if (typeof window !== 'undefined' && nextStatus === 'writing') {
                startFakeProgress(95, 320);
                startUploadTimeout();
            }
        }

        if (e.kind === 'result') {
            stopFakeProgress();
            stopUploadTimeout();
            if (e.success) {
                nextStatus = 'success';
                nextProgress = 100;
                nextMessage = e.message ?? '書き込み完了！';
            } else {
                nextStatus = 'error';
                nextMessage = e.errorMessage ?? e.message ?? 'エラー';
            }
            // bridge からの実際の結果は timeout による error 状態を上書きする
            // isUploading の管理は BoardToolbar の useEffect に任せる
            set({
                uploadStatus: nextStatus,
                uploadProgress: clamp(nextProgress, 0, 100),
                uploadMessage: nextMessage,
                uploadQueueAhead: null,
                activeCompileJobId: null,
            });
            return;
        }

        if (STATUS_ORDER[nextStatus] < STATUS_ORDER[st.uploadStatus]) return;
        if (nextStatus !== 'success') nextProgress = Math.min(nextProgress, 99);
        if (nextStatus === 'success') nextProgress = 100;
        if (nextStatus !== 'error') nextProgress = Math.max(st.uploadProgress, nextProgress);

        set({
            uploadStatus: nextStatus,
            uploadProgress: clamp(nextProgress, 0, 100),
            uploadMessage: nextMessage,
            // compiling以外のフェーズに移ったら待ち人数バッジは消す(「コンパイルのところだけ」の表示範囲)
            ...(nextStatus !== 'compiling' ? { uploadQueueAhead: null, activeCompileJobId: null } : {}),
        });
    },

    appendMonitorMessage: (data, isSent) => {
        const timestamp = formatTimestamp();
        set((state) => ({
            monitorMessages: [...state.monitorMessages, { timestamp, data, isSent: isSent || false }],
        }));
    },

    appendMonitorMessageBatch: (messages) => {
        const timestamp = formatTimestamp();
        const newMessages = messages.map((data) => ({ data, timestamp, isSent: false }));
        pendingMessages.push(...newMessages);
        if (!rafId) {
            rafId = requestAnimationFrame(() => {
                const MAX_MONITOR_MESSAGES = 5000;
                const state = useBoardEditorStore.getState();
                const next = [...state.monitorMessages, ...pendingMessages];
                const capped =
                    next.length > MAX_MONITOR_MESSAGES ? next.slice(next.length - MAX_MONITOR_MESSAGES) : next;
                const addedCount = pendingMessages.length;
                set({
                    monitorMessages: capped,
                    serialReceivedCount: state.serialReceivedCount + addedCount,
                });
                pendingMessages = [];
                rafId = null;
            });
        }
    },

    appendSerialChunk: (chunk: string, onLines?: (lines: string[]) => void) => {
        pendingSerialChunks.push(chunk);
        if (serialRafId !== null) return;
        serialRafId = requestAnimationFrame(() => {
            serialRafId = null;
            const combined = pendingSerialChunks.join('');
            pendingSerialChunks = [];

            const state = get();
            const raw = state.pendingSerialLine + combined.replace(/\r/g, '');
            const rawParts = raw.split('\n');
            const nextPending = rawParts.pop() ?? '';
            const timestamp = formatTimestamp();
            const lineTimestamp = state.pendingSerialLineTimestamp || timestamp;

            // monitor_bluetoothブロックがBT名を名乗るために送るこのプレフィックス行は、
            // ブリッジ経由の場合はOS由来で既にBT名が分かっている(detectedBoards.bluetooth_name)
            // ため使い道が無く、そのままだと2秒おきのノイズとしてログに出てしまう。
            // Web Serial APIパス側はuseMonitorWebSerialApi.tsが別途この行を検出・利用しているので、
            // このstoreの表示用ログには一律で出さない。
            const parts = rawParts.filter((line) => !line.startsWith(BT_NAME_ANNOUNCE_PREFIX));

            const newMessages: MonitorMessage[] = parts.map((line) => ({
                timestamp: lineTimestamp,
                data: line,
                isSent: false,
            }));

            const MAX_MONITOR_MESSAGES = 5000;
            const combined_messages = [...state.monitorMessages, ...newMessages];
            const capped =
                combined_messages.length > MAX_MONITOR_MESSAGES
                    ? combined_messages.slice(combined_messages.length - MAX_MONITOR_MESSAGES)
                    : combined_messages;

            set({
                monitorMessages: capped,
                pendingSerialLine: nextPending,
                pendingSerialLineTimestamp: nextPending
                    ? state.pendingSerialLine === ''
                        ? timestamp
                        : state.pendingSerialLineTimestamp
                    : '',
                serialReceivedCount: state.serialReceivedCount + newMessages.length,
            });

            if (parts.length > 0) {
                onLines?.(parts);
            }
        });
    },

    clearSerialChunks: () => {
        pendingSerialChunks = [];
        set({ pendingSerialLine: '', pendingSerialLineTimestamp: '' });
    },

    clearMonitorMessages: () =>
        set({
            monitorMessages: [],
            pendingSerialLine: '',
            pendingSerialLineTimestamp: '',
            serialReceivedCount: 0,
        }),
    setMonitorAutoScroll: (enabled) => set({ monitorAutoScroll: enabled }),
    setIsMonitorOpen: (flag) => set({ isMonitorOpen: flag }),

    resetEditor: () => {
        stopFakeProgress();
        stopUploadTimeout();
        set({
            code: getInitialCode('orange'),
            board: 'orange',
            port: null,
            hasSelectedBoard: false,
            detectedBoards: [],
            logs: [],
            isUploading: false,
            uploadProgress: 0,
            uploadStatus: 'checking',
            uploadMessage: '',
            uploadQueueAhead: null,
            activeCompileJobId: null,
            activeUploadRequestId: null,
            lastBuild: null,
            isBuilding: false,
            monitorMessages: [],
            pendingSerialLine: '',
            pendingSerialLineTimestamp: '',
            serialReceivedCount: 0,
            blockZoomScale: 0.8,
        });
    },
}));
